// pages/dataAnalyst/kraWork/kraWorkFormat.js
//
// Pure helpers for "My KRA Work" and for the source links on My Performance.
// Every number shown comes from the API (GET /analyst-work/...); nothing is
// calculated here except display text and link targets.

// Where each record type lives on the My KRA Work page, and the action label
// a KPI card on My Performance uses to open it.
export const SOURCE_LINK_LABELS = {
  ERP_TRAINING_SESSION: "View Training Logs",
  DATA_VALIDATION: "Open Data Quality Tracker",
  DATA_UPDATE: "Open Data Quality Tracker",
  AUDIT_OBSERVATION: "Open Data Quality Tracker",
  MIS_SUBMISSION: "Open MIS Tracker",
  DASHBOARD_ACTIVITY: "Open MIS Tracker",
  REPORT_ACCURACY: "Open MIS Tracker",
  SCHOOL_DATA_SUBMISSION: "Open School Coordination",
  SCHOOL_SUPPORT_REQUEST: "Open School Coordination",
  SOP_REVIEW: "Open SOP Tracker",
  AUTOMATION_INITIATIVE: "Open SOP Tracker",
};

export const SECTION_OF_TYPE = {
  ERP_TRAINING_SESSION: "ERP_SUPPORT",
  DATA_VALIDATION: "DATA_QUALITY",
  DATA_UPDATE: "DATA_QUALITY",
  AUDIT_OBSERVATION: "DATA_QUALITY",
  MIS_SUBMISSION: "MIS_REPORTING",
  DASHBOARD_ACTIVITY: "MIS_REPORTING",
  REPORT_ACCURACY: "MIS_REPORTING",
  SCHOOL_DATA_SUBMISSION: "SCHOOL_COORDINATION",
  SCHOOL_SUPPORT_REQUEST: "SCHOOL_COORDINATION",
  SOP_REVIEW: "SOP_PROCESS",
  AUTOMATION_INITIATIVE: "SOP_PROCESS",
};

// "/data-analyst" from "/data-analyst/performance" -- routes are built from
// the user's raw role string (routes/DynamicRoutes.jsx), so the prefix is
// read from the current URL instead of being hard-coded.
export function rolePrefix(pathname = (typeof window !== "undefined" ? window.location.pathname : "")) {
  const first = String(pathname || "").split("/").filter(Boolean)[0];
  return `/${first || "data-analyst"}`;
}

export function kraWorkLink({ recordType, month, add = false, pathname } = {}) {
  const params = new URLSearchParams();
  if (recordType && SECTION_OF_TYPE[recordType]) params.set("section", SECTION_OF_TYPE[recordType]);
  if (recordType) params.set("type", recordType);
  if (month) params.set("month", month);
  if (add) params.set("add", "1");
  const qs = params.toString();
  return `${rolePrefix(pathname)}/kra-work${qs ? `?${qs}` : ""}`;
}

// Record lock / evidence state, as the analyst sees it.
export const RECORD_EVIDENCE_META = {
  NONE: { label: "Not in evidence yet", tone: "gray" },
  DRAFT: { label: "In draft evidence", tone: "gray" },
  SUBMITTED: { label: "Awaiting review", tone: "blue" },
  UNDER_REVIEW: { label: "Awaiting review", tone: "blue" },
  NEEDS_CHANGES: { label: "Changes requested", tone: "amber" },
  APPROVED: { label: "Approved", tone: "green" },
  REJECTED: { label: "Rejected", tone: "red" },
};

export function recordEvidenceMeta(record) {
  return RECORD_EVIDENCE_META[record?.evidence_status || "NONE"] || RECORD_EVIDENCE_META.NONE;
}

export function statusLabel(typeMeta, value) {
  return typeMeta?.statuses?.find((s) => s.value === value)?.label || value || "—";
}

// The primary date shown in the list for a record.
export function primaryDate(record) {
  return record.record_date || record.due_date || (record.request_at ? String(record.request_at).slice(0, 16) : null) || record.period_month;
}

const trim = (n) => (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));

// Month preview of what the records add up to: "103 / 110 (93.64%)" or "3".
export function formatPreview(preview) {
  if (!preview) return null;
  if (preview.denominator !== null && preview.denominator !== undefined) {
    if (!preview.denominator) return "Nothing due yet";
    const pct = Math.round((Number(preview.numerator) / Number(preview.denominator)) * 10000) / 100;
    return `${trim(preview.numerator)} / ${trim(preview.denominator)} (${trim(pct)}%)`;
  }
  if (preview.count !== null && preview.count !== undefined) return trim(preview.count);
  return null;
}

// Field value as text for the read-only view.
export function formatFieldValue(field, value, { schools = [], record } = {}) {
  if (value === null || value === undefined || value === "") return "—";
  if (field.type === "SCHOOL") return record?.school_name || schools.find((s) => Number(s.id) === Number(value))?.name || `#${value}`;
  if (field.type === "SELECT") return field.options?.find((o) => o.value === value)?.label || value;
  if (field.type === "BOOLEAN") return value ? "Yes" : "No";
  if (field.type === "DATETIME") return String(value).replace("T", " ").slice(0, 16);
  return String(value);
}

// The form value for an input, from a record (typed column or details).
export function recordValue(record, field) {
  if (!record) return field.type === "BOOLEAN" ? false : "";
  const raw = Object.prototype.hasOwnProperty.call(record, field.key) ? record[field.key] : record.details?.[field.key];
  if (raw === null || raw === undefined) return field.type === "BOOLEAN" ? false : "";
  if (field.type === "DATETIME") return String(raw).replace(" ", "T").slice(0, 16);
  return raw;
}
